import {Box, TextField, Chip, Typography} from '@mui/material'
import React from 'react'
import {useTranslation} from 'react-i18next'

interface UsageInputProps {
  selectedUsage?: string
  onUsageSelect: (usage: string) => void
  availableUsages: string[]
  'data-test-id'?: string
}

const MAX_USAGE_LENGTH = 72

/**
 * Component that displays a text input field with pill button suggestions for usage selection
 * Redesigned to match requirements: text input with suggestion pills below
 */
export const UsageInput: React.FC<UsageInputProps> = ({
  selectedUsage,
  onUsageSelect,
  availableUsages,
  'data-test-id': dataTestId = 'usage-input'
}) => {
  const {t} = useTranslation()
  // Map usage codes to user-friendly labels
  const getUsageLabel = (usageCode: string) => {
    const usageMap: Record<string, string> = {
      rawPour: 'Raw Pour',
      floor: 'Floor',
      balcony: 'Balcony',
      boardPiles: 'Board piles',
      bridge: 'Bridge',
      ceiling: 'Ceiling',
      insulation: 'Insulation',
      elevator: 'Elevator',
      wall: 'Wall',
      fillingHoles: 'Filling Holes',
      polishedFloor: 'Polished Floor'
    }

    const fallback = usageMap[usageCode]
    return fallback
      ? t(`orderIntake.rmc.orderManagement.usageOptions.${usageCode}`, fallback)
      : usageCode
  }

  // Handle pill button click - sets the input value
  const handlePillClick = (usageCode: string) => {
    const label = getUsageLabel(usageCode)
    onUsageSelect(label) // Set the actual usage value
  }

  // Handle input change
  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value
    onUsageSelect(value) // Set the actual usage value
  }

  return (
    <Box sx={{width: '100%'}} data-test-id={dataTestId}>
      {/* Text Input Field */}
      <TextField
        fullWidth
        placeholder={t('orderIntake.rmc.orderManagement.usagePlaceholder', 'Enter usage description...')}
        value={selectedUsage || ''}
        onChange={handleInputChange}
        variant="outlined"
        inputProps={{
          maxLength: MAX_USAGE_LENGTH
        }}
        sx={{
          mb: 3,
          '& .MuiOutlinedInput-root': {
            fontFamily: 'Inter',
            fontSize: '16px',
            backgroundColor: '#ffffff',
            '&:hover fieldset': {
              borderColor: '#016ad4'
            },
            '&.Mui-focused fieldset': {
              borderColor: '#016ad4'
            }
          },
          '& .MuiInputBase-input': {
            padding: '12px 16px'
          }
        }}
        data-test-id={`${dataTestId}-input`}
      />

      {/* Character count helper text */}
      <Typography
        variant="body2"
        sx={{
          color: '#52667a',
          fontSize: '14px',
          mb: 2,
          textAlign: 'right'
        }}
      >
        {t('orderIntake.rmc.orderManagement.usageCharacterCount', '{{current}}/{{max}} characters', {
          current: (selectedUsage || '').length,
          max: MAX_USAGE_LENGTH
        })}
      </Typography>

      {/* Suggestion Pills */}
      {availableUsages.length > 0 && (
        <Box>
          <Typography
            variant="body2"
            sx={{
              color: '#29333d',
              fontSize: '14px',
              fontWeight: 500,
              mb: 2
            }}
          >
            {t('orderIntake.rmc.orderManagement.usageSuggestions', 'Suggestions:')}
          </Typography>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1
            }}
          >
            {availableUsages.map((usageCode) => {
              const label = getUsageLabel(usageCode)
              return (
                <Chip
                  key={usageCode}
                  label={label}
                  clickable
                  onClick={() => handlePillClick(usageCode)}
                  sx={{
                    backgroundColor: '#f2f8fd',
                    color: '#016ad4',
                    border: '1px solid #016ad4',
                    fontFamily: 'Inter',
                    fontSize: '14px',
                    fontWeight: 400,
                    '&:hover': {
                      backgroundColor: '#e6f3ff'
                    },
                    '&:focus': {
                      backgroundColor: '#e6f3ff'
                    }
                  }}
                  data-test-id={`${dataTestId}-pill-${usageCode}`}
                />
              )
            })}
          </Box>
        </Box>
      )}
    </Box>
  )
}
